import { Suspense, lazy, useEffect, useState, type ComponentType } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Moon, Search, Star, Sun } from 'lucide-react';

import { api } from './lib/api';
import { useAuth } from './lib/auth';
import { useApi, useLocalState, useStream } from './hooks/useApi';
import { WatchlistProvider, useWatchlist } from './lib/watchlist';
import { useTheme } from './lib/theme';
import { IconBandage, IconChart, IconInfo, IconLedger, IconLineup, IconTarget } from './components/Icons';
import { Dialog, Dropdown, PageSkeleton, Tabs, TooltipProvider } from './components/ui';
import { BetSlipProvider } from './components/BetSlip';
import { AlertBell } from './components/AlertBell';
import { WatchlistBody, WatchlistPanel } from './components/Watchlist';
import { CommandSearch, type SearchPage } from './components/CommandSearch';

// Each page is its own chunk: the charting libraries only load where a chart is.
const Markets = lazy(() => import('./pages/Markets'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const SharpPicks = lazy(() => import('./pages/SharpPicks'));
const Injuries = lazy(() => import('./pages/Injuries'));
const Rosters = lazy(() => import('./pages/Rosters'));
const News = lazy(() => import('./pages/News'));
const ModelLab = lazy(() => import('./pages/ModelLab'));
const BetLedger = lazy(() => import('./pages/BetLedger'));
const Settings = lazy(() => import('./pages/Settings'));
const GamePage = lazy(() => import('./pages/GamePage'));
const TradingFloor = lazy(() => import('./features/tradingFloor/TradingFloorPage'));
const Feed = lazy(() => import('./pages/Feed'));
const PlayerProps = lazy(() => import('./pages/PlayerProps'));
const Fantasy = lazy(() => import('./pages/Fantasy'));
const Overview = lazy(() => import('./pages/Overview'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminHealth = lazy(() => import('./pages/AdminHealth'));
const AlertSettings = lazy(() => import('./pages/account/AlertSettings'));
const DevEmails = lazy(() => import('./pages/account/DevEmails'));
const OverflowInspector = lazy(() => import('./dev/OverflowInspector').then((m) => ({ default: m.OverflowInspector })));

type Section = {
  key: string;
  label: string;
  to: string;
  Icon: ComponentType<{ className?: string; size?: number }>;
  /** Path prefixes that belong to this section. */
  match: string[];
  /** Second-level tabs; a section with its own navigation (Fantasy) has none. */
  tabs?: Array<{ to: string; label: string }>;
};

/** The five desks. Every page belongs to exactly one. */
const SECTIONS: Section[] = [
  {
    key: 'markets', label: 'Games', to: '/', Icon: IconChart,
    match: ['/', '/trading-floor', '/dashboard', '/overview', '/feed', '/game'],
    tabs: [
      { to: '/', label: 'All games' },
      { to: '/trading-floor', label: 'Trading Floor' },
      { to: '/dashboard', label: 'Briefing' },
      { to: '/overview', label: 'Overview' },
      { to: '/feed', label: 'Feed' },
    ],
  },
  {
    key: 'edges', label: 'Picks', to: '/picks', Icon: IconTarget,
    match: ['/picks', '/props', '/model'],
    tabs: [
      { to: '/picks', label: 'Game picks' },
      { to: '/props', label: 'Player Props' },
      { to: '/model', label: 'Model Lab' },
    ],
  },
  { key: 'fantasy', label: 'Fantasy', to: '/fantasy', Icon: IconLineup, match: ['/fantasy'] },
  {
    key: 'tools', label: 'Research', to: '/injuries', Icon: IconBandage,
    match: ['/injuries', '/rosters', '/news'],
    tabs: [
      { to: '/injuries', label: 'Injuries' },
      { to: '/rosters', label: 'Rosters' },
      { to: '/news', label: 'News' },
    ],
  },
  { key: 'ledger', label: 'My Bets', to: '/ledger', Icon: IconLedger, match: ['/ledger'] },
];

function sectionFor(path: string): Section | null {
  for (const s of SECTIONS) {
    if (s.match.some((m) => (m === '/' ? path === '/' : path === m || path.startsWith(`${m}/`)))) return s;
  }
  return null;
}

const SEARCH_PAGES: SearchPage[] = [
  ...SECTIONS.flatMap((s) => (s.tabs ?? [{ to: s.to, label: s.label }]).map((t) => ({ to: t.to, label: t.label, section: s.label }))),
  { to: '/settings', label: 'Settings', section: 'Account' },
  { to: '/account/alerts', label: 'Alerts', section: 'Account' },
];

/** Wide screens get the watchlist as a column; everything else opens it as a sheet. */
function useWide(): boolean {
  const query = '(min-width: 1280px)';
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}

export default function App() {
  return (
    <TooltipProvider>
      <WatchlistProvider>
        <BetSlipProvider>
          <Shell />
        </BetSlipProvider>
      </WatchlistProvider>
    </TooltipProvider>
  );
}

function Shell() {
  const location = useLocation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const { user, signOut } = useAuth();
  const { items: watchItems } = useWatchlist();
  const { theme, toggle: toggleTheme } = useTheme();
  const [searchOpen, setSearchOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useLocalState('shell.watchlist.v2', false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const wide = useWide();
  const [resend, setResend] = useState<'idle' | 'sending' | 'sent' | string>('idle');
  const health = useApi(() => api.health(), []);
  const { connected } = useStream();

  // The phone sheet closes once a row in it has been followed.
  useEffect(() => setSheetOpen(false), [location.pathname]);

  const section = sectionFor(location.pathname);
  const espn = health.data?.espn;

  // ⌘K / Ctrl+K opens search from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  async function resendVerification() {
    setResend('sending');
    try {
      await api.resendVerification();
      setResend('sent');
    } catch (err) {
      setResend(err instanceof Error ? err.message : 'Could not send');
    }
  }

  async function refreshAll() {
    await api.refresh();
    // A full reload is the simplest way to re-pull every panel at once.
    window.location.reload();
  }

  const toggleWatchlist = () => (wide ? setPanelOpen(!panelOpen) : setSheetOpen(true));
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <div className="app">
      <header className="topbar">
        <NavLink to="/" className="logo" aria-label="DeltaPicks home">
          <span className="logo-mark" aria-hidden>Δ</span>
          <span>DeltaPicks</span>
          <span className="logo-sub">{espn ? `Wk ${espn.week}` : ''}</span>
        </NavLink>

        <nav className="main-tabs" aria-label="Main">
          {SECTIONS.map((s) => (
            <NavLink key={s.key} to={s.to} className={`main-tab ${section?.key === s.key ? 'active' : ''}`}>
              {s.label}
            </NavLink>
          ))}
        </nav>

        <button className="search-btn" onClick={() => setSearchOpen(true)} aria-label="Search">
          <Search size={16} strokeWidth={1.75} />
          <span className="search-label">Search teams, players, props</span>
          <kbd>{isMac ? '⌘K' : 'Ctrl K'}</kbd>
        </button>

        {health.data && !espn?.ok && <span className="pill bad">ESPN unreachable</span>}

        <button
          className={`btn btn-ghost btn-sm watch-toggle ${wide && panelOpen ? 'active' : ''}`}
          onClick={toggleWatchlist}
          aria-label="Watchlist"
          title="Watchlist"
        >
          <Star size={16} strokeWidth={1.75} />
          <span className="num">{watchItems.length || ''}</span>
        </button>

        <AlertBell />

        <button
          className="btn btn-ghost btn-sm icon-btn"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
        >
          {theme === 'dark' ? <Sun size={16} strokeWidth={1.75} /> : <Moon size={16} strokeWidth={1.75} />}
        </button>

        <span
          className={`stream-dot ${connected ? 'on' : ''}`}
          title={connected ? 'Live: prices update on their own' : 'Reconnecting to live updates'}
          aria-label={connected ? 'Live updates on' : 'Live updates reconnecting'}
        />

        <Dropdown
          trigger={
            <button className="avatar" aria-label="Account menu">
              {(user?.email ?? '?').charAt(0).toUpperCase()}
            </button>
          }
          items={[
            { type: 'label', label: user?.email ?? '' },
            { label: 'Settings', onSelect: () => navigate('/settings') },
            { label: 'Alerts', onSelect: () => navigate('/account/alerts') },
            ...(user?.isAdmin ? [{ label: 'Admin', onSelect: () => navigate('/admin') }] : []),
            { label: 'Refresh data', onSelect: () => void refreshAll() },
            { type: 'separator' as const },
            { label: 'Sign out', onSelect: signOut, danger: true },
          ]}
        />
      </header>

      {user && !user.emailVerified && (
        <div className="verify-banner" role="status">
          <IconInfo size={16} />
          <span>
            Confirm your email — we sent a link to <strong>{user.email}</strong>.
          </span>
          {resend === 'sent' ? (
            <span className="dim">New link sent.</span>
          ) : (
            <button className="btn btn-sm" onClick={resendVerification} disabled={resend === 'sending'}>
              {resend === 'sending' ? 'Sending…' : 'Resend link'}
            </button>
          )}
          {resend !== 'idle' && resend !== 'sending' && resend !== 'sent' && <span className="neg">{resend}</span>}
        </div>
      )}

      <div className="shell-body">
        <main className="content">
          {section?.tabs && (
            <Tabs
              className="subnav"
              items={section.tabs.map((t) => ({ value: t.to, label: t.label }))}
              // A game page lives under the board it was opened from.
              value={
                section.tabs.find((t) =>
                  t.to === '/' ? location.pathname === '/' || location.pathname.startsWith('/game/') : location.pathname.startsWith(t.to),
                )?.to ?? ''
              }
              onChange={(to) => navigate(to)}
            />
          )}

          {import.meta.env.DEV && location.search.includes('overflow') && (
            <Suspense fallback={null}>
              <OverflowInspector />
            </Suspense>
          )}

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              // The Trading Floor swaps games in place, keeping its rail and chart.
              key={location.pathname.startsWith('/trading-floor') ? '/trading-floor' : location.pathname}
              initial={reduceMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0 }}
              transition={{ duration: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <Suspense fallback={<PageSkeleton />}>
              <Routes location={location}>
                <Route path="/" element={<Markets />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/overview" element={<Overview />} />
                <Route path="/picks" element={<SharpPicks />} />
                <Route path="/game/:gameId" element={<GamePage />} />
                <Route path="/trading-floor" element={<TradingFloor />} />
                <Route path="/trading-floor/:gameId" element={<TradingFloor />} />
                <Route path="/props" element={<PlayerProps />} />
                <Route path="/fantasy/*" element={<Fantasy />} />
                <Route path="/feed" element={<Feed />} />
                <Route path="/injuries" element={<Injuries />} />
                <Route path="/rosters" element={<Rosters />} />
                <Route path="/rosters/:teamId" element={<Rosters />} />
                <Route path="/news" element={<News />} />
                <Route path="/model" element={<ModelLab />} />
                <Route path="/ledger" element={<BetLedger />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/account/alerts" element={<AlertSettings />} />
                <Route path="/dev/emails" element={<DevEmails />} />
                {user?.isAdmin && <Route path="/admin" element={<Admin />} />}
                {user?.isAdmin && <Route path="/admin/health" element={<AdminHealth />} />}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
            </motion.div>
          </AnimatePresence>

          <p className="shell-foot">
            DeltaPicks is a research aid, not advice. Verify every line and injury at your book before you bet. <a href="/privacy" style={{ textDecoration: 'underline' }}>Privacy</a>
          </p>
        </main>

        {wide && panelOpen && <WatchlistPanel />}
      </div>

      <nav className="bottom-tabs" aria-label="Main">
        {SECTIONS.map((s) => (
          <NavLink key={s.key} to={s.to} className={`bottom-tab ${section?.key === s.key ? 'active' : ''}`}>
            <s.Icon size={20} />
            {s.label}
          </NavLink>
        ))}
      </nav>

      <Dialog open={sheetOpen && !wide} onOpenChange={setSheetOpen} title="Watchlist" variant="sheet">
        <WatchlistBody />
      </Dialog>

      <CommandSearch open={searchOpen} onOpenChange={setSearchOpen} pages={SEARCH_PAGES} />
    </div>
  );
}
